import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ROLES, ROLE_LABELS } from '@recica/shared/permissions';
import { useAuth } from '../auth/useAuth.js';
import { Badge } from '../components/ui/Badge.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Card } from '../components/ui/Card.jsx';
import { Modal } from '../components/ui/Modal.jsx';
import { Spinner } from '../components/ui/Spinner.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { initials } from '../utils/formatters.js';
import { usersApi } from '../services/usersApi.js';

import { UserCreateModal } from './UserCreateModal.jsx';

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3.5 8.5l3 3 6-7" />
    </svg>
  );
}

function Chevron({ open }) {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform var(--duration-fast) var(--ease-out)' }} aria-hidden="true">
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

export function UserManagementCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  /* Die eigene Kennung, damit der Knopf zum Zuruecksetzen an der eigenen
     Zeile gar nicht erst erscheint. */
  const { user: angemeldet } = useAuth();
  const ownUserId = angemeldet?.sub || angemeldet?.id || null;
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const users = useQuery({ queryKey: ['users'], queryFn: usersApi.list });

  /* Kennwort zuruecksetzen. Zurueck kommt ein EINMAL-LINK, kein Kennwort:
     der Administrator soll den Zugang wiederherstellen koennen, nicht das
     Konto uebernehmen. Deshalb steht das Ergebnis in einem Dialog, den man
     ausdruecklich schliesst - der Link ist nur einmal zu sehen. */
  const [zurueckgesetzt, setZurueckgesetzt] = useState(null);
  const [resetFehler, setResetFehler] = useState(null);
  const [kopiert, setKopiert] = useState(false);
  const zuruecksetzen = useMutation({
    mutationFn: (id) => usersApi.resetPassword(id),
    onSuccess: (data) => {
      setResetFehler(null);
      setKopiert(false);
      setZurueckgesetzt(data);
    },
    onError: (err) => setResetFehler(err?.message || t('common.loadError')),
  });

  const updateRoles = useMutation({
    mutationFn: ({ id, roles }) => usersApi.update(id, { roles }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }) => usersApi.update(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const rows = useMemo(() => {
    const all = users.data || [];
    const q = query.trim().toLowerCase();
    return all.filter((u) => {
      if (roleFilter && !u.roles.includes(roleFilter)) return false;
      if (q && !`${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [users.data, query, roleFilter]);

  if (users.isLoading) {
    return <Card><Spinner /></Card>;
  }

  function toggleRole(user, role) {
    const has = user.roles.includes(role);
    const roles = has ? user.roles.filter((r) => r !== role) : [...user.roles, role];
    updateRoles.mutate({ id: user.id, roles });
  }
  function toggleExpand(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const total = (users.data || []).length;

  return (
    <Card>
      <div className="user-admin-head">
        <input
          className="input user-search"
          type="search"
          placeholder={t('settings.users.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select className="input user-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">{t('settings.users.allRoles')}</option>
          {ROLES.map((role) => <option key={role} value={role}>{t(ROLE_LABELS[role])}</option>)}
        </select>
        <span className="muted text-sm user-count">
          {roleFilter || query ? t('settings.users.countFiltered', { shown: rows.length, total }) : t('settings.users.count', { count: total })}
        </span>
        <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('settings.users.new')}</Button>
      </div>

      <div className="user-admin-list">
        {rows.map((user) => {
          const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
          const isOpen = expanded.has(user.id);
          return (
            <div key={user.id} className={`user-row${user.isActive ? '' : ' is-inactive'}${isOpen ? ' is-open' : ''}`}>
              <div className="user-row-main">
                <div className="avatar avatar-lg" aria-hidden="true">{initials(user.firstName || user.email, user.lastName || '')}</div>
                <div className="user-card-identity">
                  <span className="user-card-name">{name}</span>
                  <span className="user-card-email">{user.email}</span>
                </div>

                <div className="user-row-roles">
                  {user.roles.length === 0
                    ? <span className="role-badge is-empty">{t('settings.users.noRoles')}</span>
                    : user.roles.map((role) => <span key={role} className="role-badge">{t(ROLE_LABELS[role])}</span>)}
                </div>

                <Badge variant={user.isActive ? 'success' : 'neutral'}>
                  {user.isActive ? t('settings.users.active') : t('settings.users.inactive')}
                </Badge>

                <div className="user-row-actions">
                  <button type="button" className={`user-row-edit${isOpen ? ' is-on' : ''}`} aria-expanded={isOpen} onClick={() => toggleExpand(user.id)}>
                    {t('settings.users.editRoles')} <Chevron open={isOpen} />
                  </button>
                  <Button size="sm" variant="ghost" onClick={() => toggleActive.mutate({ id: user.id, isActive: !user.isActive })}>
                    {user.isActive ? t('settings.users.deactivate') : t('settings.users.activate')}
                  </Button>
                  {/* Das eigene Kennwort wird gewechselt, nicht zurueckgesetzt -
                      der Dienst weist es ohnehin ab, aber ein Knopf, der immer
                      scheitert, ist ein schlechter Knopf. */}
                  {user.id !== ownUserId && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={zuruecksetzen.isPending}
                      onClick={() => zuruecksetzen.mutate(user.id)}
                    >
                      {t('settings.users.resetPassword')}
                    </Button>
                  )}
                </div>
              </div>

              {isOpen && (
                <div className="role-chips">
                  {ROLES.map((role) => {
                    const on = user.roles.includes(role);
                    return (
                      <button key={role} type="button" className={`role-chip${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => toggleRole(user, role)}>
                        {on && <CheckIcon />}
                        <span>{t(ROLE_LABELS[role])}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {rows.length === 0 && <p className="muted" style={{ padding: 'var(--space-4)' }}>{t('settings.users.empty')}</p>}
      </div>

      {resetFehler && <div className="alert danger">{resetFehler}</div>}

      <Modal
        open={Boolean(zurueckgesetzt)}
        title={t('settings.users.resetTitle')}
        onClose={() => setZurueckgesetzt(null)}
        footer={
          <Button variant="primary" onClick={() => setZurueckgesetzt(null)}>
            {t('common.close')}
          </Button>
        }
      >
        {zurueckgesetzt && (
          <>
            <p>{t('settings.users.resetDone', { email: zurueckgesetzt.email })}</p>
            {/* Der Link steht hier EINMAL. Er landet weder im Protokoll noch in
                einer Liste - wer ihn lesen kann, koennte damit das Kennwort
                setzen. */}
            <label className="field">
              <span className="field-label">{t('settings.users.resetLink')}</span>
              <input className="input mono" readOnly value={zurueckgesetzt.url} onFocus={(e) => e.target.select()} />
            </label>
            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
              <Button
                size="sm"
                onClick={() => {
                  navigator.clipboard?.writeText(zurueckgesetzt.url).then(
                    () => setKopiert(true),
                    () => setKopiert(false),
                  );
                }}
              >
                {kopiert ? t('common.copied') : t('common.copy')}
              </Button>
              <span className="muted text-sm">{t('settings.users.resetExpires')}</span>
            </div>
            <p className="muted text-sm">{t('settings.users.resetWarning')}</p>
          </>
        )}
      </Modal>

      <UserCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </Card>
  );
}
